import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import FormTextField from 'components/IAP/formFields/FormTextField';
import FormTextAreaField from 'components/IAP/formFields/FormTextAreaField';
import {
  useCreateResourceRequest,
  ResourceRequestItem,
} from 'components/IAP/hooks/incidentResourceRequestHooks';
import { Resource, ApprovalSteps } from './RequestCard';
import { PratusStepper } from 'components/DesignSystems/PratusStepper/PratusStepper';
import { AddRequestedResource } from './AddRequestedResource';
import { SectionReqContent } from './SectionReqContent';
import { useWorkAssignments } from 'components/IAP/hooks/workAssignmentHooks';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useAppSelector, selectUser } from 'slices/commonSelectors';

type RequestFormProps = {
  name: string;
  items: string[];
  capability_enabled: string;
};

export const AddRequestCard: React.FC<{
  setShowAddResourceCard: (show: boolean) => void;
}> = ({ setShowAddResourceCard }) => {
  const createResourceRequest = useCreateResourceRequest();
  const [showAdd, setShowAdd] = useState(false);
  const [editResourceIndex, setEditResourceIndex] = useState<number | null>(
    null
  );
  const [workAssignment, setWorkAssignment] = useState<string | null>(null);
  const [customResources, setCustomResources] = useState<ResourceRequestItem[]>(
    []
  );
  const { data: workAssignments } = useWorkAssignments();
  const user = useAppSelector(selectUser);
  const form = useForm<RequestFormProps>({
    mode: 'onChange',
  });

  const onSubmit = async (formData: RequestFormProps) => {
    const items = customResources.map((resource) => ({
      ...resource,
      unit: 'unit',
      status: 'pending' as ResourceRequestItem['status'],
    }));
    createResourceRequest.mutateAsync({
      ...formData,
      items,
      requestor_name: user?.user_name || user?.email_address,
    });
    setShowAddResourceCard(false);
  };

  const closeResourceEditor = () => {
    setShowAdd(false);
    setEditResourceIndex(null);
    setWorkAssignment(null);
  };

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="resource-add-card p-3">
            <FormTextField title="Enter Request Name" fieldName="name" />
            <PratusStepper steps={ApprovalSteps} activeStep={1} />
            <br />
            <div className="border rounded p-2 mb-2">
              <div className="d-flex align-items-center gap-2 mb-2">
                <div>Requested Resources</div>
                <StylishNewButton
                  className="button button--secondary"
                  onClick={() => {
                    setEditResourceIndex(null);
                    setWorkAssignment(null);
                    setShowAdd(!showAdd);
                  }}
                >
                  + Add Item to Request
                </StylishNewButton>
                <div className="d-flex border rounded border-neutral-500">
                  <div className="d-flex align-items-center gap-1">
                    <SharedIcon iconName="auto_awesome" />
                    Autofill From Work Assignment:
                  </div>
                  <StylishNewSelect
                    options={workAssignments?.map((wa) => ({
                      label: wa.work_assignment,
                      value: wa.id,
                    }))}
                    onChange={(value) => {
                      if (!value) {
                        setWorkAssignment(null);
                        return;
                      }
                      const selectedWorkAssignment = workAssignments?.find(
                        (wa) => wa.id === value.value
                      );
                      if (selectedWorkAssignment) {
                        setEditResourceIndex(null);
                        setShowAdd(true);
                        setWorkAssignment(
                          selectedWorkAssignment.work_assignment
                        );
                      }
                    }}
                    isClearable
                  />
                </div>
              </div>
              {showAdd && editResourceIndex === null && (
                <AddRequestedResource
                  setCustomResources={setCustomResources}
                  workAssignment={
                    editResourceIndex === null ? workAssignment : null
                  }
                  setShowAdd={setShowAdd}
                  resourceToEdit={
                    editResourceIndex !== null
                      ? customResources[editResourceIndex]
                      : undefined
                  }
                  editIndex={editResourceIndex}
                  onClose={closeResourceEditor}
                />
              )}
              {customResources.map((resource, i) =>
                showAdd && editResourceIndex === i ? (
                  <AddRequestedResource
                    key={resource.id ?? i}
                    setCustomResources={setCustomResources}
                    workAssignment={null}
                    setShowAdd={setShowAdd}
                    resourceToEdit={resource}
                    editIndex={i}
                    onClose={closeResourceEditor}
                  />
                ) : (
                  <Resource
                    key={resource.id ?? i}
                    resource={{
                      ...resource,
                      unit: 'unit',
                      status: 'pending',
                    }}
                    deletable
                    onEdit={() => {
                      setWorkAssignment(null);
                      setEditResourceIndex(i);
                      setShowAdd(true);
                    }}
                    onDelete={() => {
                      setCustomResources((prev) =>
                        prev.filter((_, index) => index !== i)
                      );

                      if (editResourceIndex === i) {
                        closeResourceEditor();
                      } else if (
                        editResourceIndex !== null &&
                        editResourceIndex > i
                      ) {
                        setEditResourceIndex(editResourceIndex - 1);
                      }
                    }}
                  />
                )
              )}
            </div>
            <div className="border rounded p-2 mb-2">
              <FormTextAreaField
                title="Suggested Sources of Supply & Substitutes"
                fieldName="supply_sources"
              />
            </div>
            <div className="border rounded p-2 mb-2">
              <FormTextAreaField
                title="Capability Enabled"
                fieldName="capability_enabled"
              />
            </div>
            <SectionReqContent memberPositions={[]} />
            <div>
              <StylishNewButton type="submit" className="me-2">
                Submit to Section Chief
              </StylishNewButton>
              <StylishNewButton
                className="button--secondary"
                onClick={() => setShowAddResourceCard(false)}
              >
                Cancel
              </StylishNewButton>
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  );
};
